import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { printGiftVoucherSlip } from "@/utils/giftVoucherPrint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [50, 100, 250, 500];
const METHODS = ["CASH", "CARD", "EFT"];

export const SellGiftVoucherDialog = ({ open, onClose, cashier, store }) => {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("CASH");
  const [tendered, setTendered] = useState("");
  const [buyer, setBuyer] = useState("");
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(null);

  useEffect(() => {
    if (open) {
      setAmount(""); setMethod("CASH"); setTendered(""); setBuyer("");
      keyRef.current = crypto.randomUUID();
    }
  }, [open]);

  const amt = parseFloat(amount) || 0;
  const cash = parseFloat(tendered) || 0;
  const change = method === "CASH" && cash > amt ? cash - amt : 0;
  const canSell = amt >= 10 && (method !== "CASH" || !tendered || cash >= amt);

  const sell = async () => {
    if (!navigator.onLine) return toast.error("Gift vouchers need an internet connection");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/gift-vouchers/sell`, {
        amount: amt, payment_method: method, cash_tendered: method === "CASH" && tendered ? cash : null,
        cashier, buyer_name: buyer || null, idempotency_key: keyRef.current,
      });
      printGiftVoucherSlip(data, store);
      toast.success(`Gift voucher R${amt.toFixed(2)} sold — ${data.code}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sell gift voucher");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="sell-gift-voucher-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-rose-600" /> Sell Gift Voucher</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Amount</Label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {PRESETS.map((p) => (
                <Button key={p} type="button" variant={amt === p ? "default" : "outline"} onClick={() => setAmount(String(p))} data-testid={`gv-preset-${p}`}>
                  R{p}
                </Button>
              ))}
            </div>
            <Input type="number" min="10" step="0.01" className="mt-2 text-lg text-center" placeholder="Custom amount (min R10)"
              value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="gv-amount-input" />
          </div>
          <div>
            <Label>Paid by</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {METHODS.map((m) => (
                <Button key={m} type="button" variant={method === m ? "default" : "outline"} onClick={() => setMethod(m)} data-testid={`gv-method-${m.toLowerCase()}`}>
                  {m}
                </Button>
              ))}
            </div>
          </div>
          {method === "CASH" && (
            <div>
              <Label>Cash tendered (optional)</Label>
              <Input type="number" step="0.01" value={tendered} onChange={(e) => setTendered(e.target.value)} data-testid="gv-tendered-input" />
              {change > 0 && <p className="mt-1 text-lg font-bold text-green-700" data-testid="gv-change">Change: R {change.toFixed(2)}</p>}
            </div>
          )}
          <div>
            <Label>For (optional)</Label>
            <Input value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="Name on the voucher" data-testid="gv-buyer-input" />
          </div>
          <p className="text-xs text-slate-500">Valid for 3 years. This money is held as vouchers owed and counts as a sale only when the voucher is spent.</p>
          <Button className="w-full h-11 bg-rose-600 hover:bg-rose-700" disabled={!canSell || busy} onClick={sell} data-testid="gv-sell-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Sell & print R ${amt.toFixed(2)} voucher`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
